// src/pages/Auth/components/ProfileCard.js

import React, { useState } from 'react';
import { Box, Avatar, IconButton, Menu, MenuItem, Divider, Typography } from '@mui/material';
import { useAuth } from 'context/AuthContext';
import { googleLogout } from '@react-oauth/google';

const ProfileCard = ({ isDarkMode, onLogout }) => {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  
  if (!user) return null;
  
  const userName = user?.name || "";
  const userEmail = user?.email || "";
  
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  
  const handleLogout = () => {
    googleLogout();
    logout();
    onLogout?.();
    handleMenuClose();
  };

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography 
        variant="body2" 
        sx={{ 
          color: isDarkMode ? "#e2e8f0" : "#666", 
          mr: 1,
          fontWeight: 500,
        }}
      >
        Welcome, {userName}
      </Typography>
      <IconButton
        onClick={handleMenuOpen}
        size="small"
        sx={{
          p: 0.5,
          '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
          },
        }}
      >
        <Avatar
          src={user?.profile_pic || undefined}  
          sx={{ 
            width: 36, 
            height: 36, 
            bgcolor: user?.profile_pic ? 'transparent' : "#219297",
            color: user?.profile_pic ? undefined : "white", 
            fontSize: "14px",
            fontWeight: "bold",
            border: user?.profile_pic ? `2px solid ${isDarkMode ? '#e2e8f0' : '#219297'}` : 'none',
          }}
        >
          {!user?.profile_pic && userName.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1.5,
            borderRadius: 2,
            overflow: "visible",
            filter: isDarkMode 
              ? "drop-shadow(0px 4px 12px rgba(0,0,0,0.3))" 
              : "drop-shadow(0px 4px 12px rgba(0,0,0,0.15))",
            minWidth: 260,
            p: 1,
            backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
            border: isDarkMode ? "1px solid #2d2d3f" : "none",
            "& .MuiAvatar-root": {
              width: 48,
              height: 48,
              ml: 1,
              mr: 1.5,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
              border: isDarkMode ? "1px solid #2d2d3f" : "none",
              borderBottom: "none",
              borderRight: "none",
            },
          },
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
      >
        <MenuItem disableRipple sx={{ py: 1.5, cursor: "default", backgroundColor: isDarkMode ? '#2d2d3f' : '#f5f5f5' }}>
          <Avatar
            src={user?.profile_pic || undefined}
            sx={{
              bgcolor: user?.profile_pic ? 'transparent' : "#219297",
              color: "white",
              fontSize: "16px",
              fontWeight: "bold",
              border: user?.profile_pic ? `2px solid ${isDarkMode ? '#e2e8f0' : '#219297'}` : 'none',
            }}
          >
            {!user?.profile_pic && userName.charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div style={{ 
              fontWeight: 600, 
              fontSize: "14px", 
              color: isDarkMode ? "#e2e8f0" : "#1a202c",
            }}>
              {userName}
            </div>
            <div style={{ 
              fontSize: "0.8rem", 
              color: isDarkMode ? "#a0aec0" : "#555",
            }}>
              {userEmail}
            </div>
          </div>
        </MenuItem>
        <Divider sx={{ 
          my: 1, 
          borderColor: isDarkMode ? "#2d2d3f" : "#e0e0e0",
        }} />
        <MenuItem
          onClick={handleLogout}
          sx={{ 
            fontWeight: 500, 
            alignContent: "center", 
            fontSize: "14px", 
            backgroundColor: isDarkMode ? '#2d2d3f' : '#f5f5f5',
            color: isDarkMode ? "#e2e8f0" : "#333",
            '&:hover': {
              backgroundColor: isDarkMode ? '#2d2d3f' : '#f5f5f5',
            },
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default React.memo(ProfileCard);